import { useMutation, useQuery } from "@tanstack/react-query";

import { treatmentPlanApi } from "../apis";
import { useToast } from "../contexts/ToastContext";
import type {
  GenerateTreatmentPlanForPatientDto,
  PaginationDto,
  TreatmentPlanDto,
  TreatmentPlanListResponseDto,
} from "../libs/cellera-sdk";

export interface GenerateTreatmentPlanResponse {
  status: string;
  treatment_plan_id: string;
}

export interface UseTreatmentPlanResult {
  generateTreatmentPlan: (
    params: GenerateTreatmentPlanForPatientDto
  ) => Promise<GenerateTreatmentPlanResponse | undefined>;
  isGenerating: boolean;
  error: string | null;
}

export interface UseTreatmentPlansListParams {
  page?: number;
  pageSize: number;
  enabled?: boolean;
  search?: string;
  generatedBy?: "all" | "you" | "practice";
  dateRange?:
    | "all_time"
    | "last_7_days"
    | "last_3_months"
    | "last_6_months"
    | "last_12_months"
    | "custom_range";
  customStartDate?: string;
  customEndDate?: string;
  sortBy?: "generated_on";
  sortOrder?: "asc" | "desc";
  nextPageToken?: string;
}

export const useTreatmentPlan = (): UseTreatmentPlanResult => {
  const { showSuccess, showError } = useToast();

  const {
    mutateAsync: generateTreatmentPlan,
    isPending: isGenerating,
    error: mutationError,
  } = useMutation({
    mutationFn: async (
      params: GenerateTreatmentPlanForPatientDto
    ): Promise<GenerateTreatmentPlanResponse> => {
      try {
        const response = await treatmentPlanApi.generateTreatmentPlanForPatient(
          {
            generateTreatmentPlanForPatientDto: params,
          }
        );

        // SDK updated - response should now have treatment_plan_id directly
        // Check both snake_case and camelCase variants
        const responseObj = response as unknown as Record<string, unknown>;
        const treatmentPlanId =
          (responseObj.treatment_plan_id as string) ||
          (responseObj.treatmentPlanId as string);

        if (!treatmentPlanId) {
          throw new Error(
            "API response does not contain treatment_plan_id. Response: " +
              JSON.stringify(response)
          );
        }

        return {
          status: (responseObj.status as string) || "SUCCEEDED",
          treatment_plan_id: treatmentPlanId,
        };
      } catch (error) {
        console.error("Failed to generate treatment plan:", error);
        throw error;
      }
    },
    onSuccess: () => {
      showSuccess(
        "Success",
        "Treatment plan generation has been initiated successfully."
      );
    },
    onError: (error) => {
      showError(
        "Error",
        error instanceof Error
          ? error.message
          : "Failed to generate treatment plan. Please try again."
      );
    },
  });

  // Transform mutation error to string
  const error = mutationError
    ? mutationError instanceof Error
      ? mutationError.message
      : "Unknown error"
    : null;

  return {
    generateTreatmentPlan: async (
      params: GenerateTreatmentPlanForPatientDto
    ): Promise<GenerateTreatmentPlanResponse | undefined> => {
      return await generateTreatmentPlan(params);
    },
    isGenerating,
    error,
  };
};

export const useTreatmentPlansList = ({
  page = 1,
  pageSize,
  enabled = true,
  search,
  generatedBy = "all",
  dateRange = "all_time",
  customStartDate,
  customEndDate,
  sortBy = "generated_on",
  sortOrder = "desc",
  nextPageToken,
}: UseTreatmentPlansListParams): TreatmentPlanListResponseDto & {
  isLoading: boolean;
} => {
  const { data, isLoading } = useQuery({
    queryKey: [
      "treatmentPlans",
      page,
      pageSize,
      search,
      generatedBy,
      dateRange,
      customStartDate,
      customEndDate,
      sortBy,
      sortOrder,
      nextPageToken,
    ],
    queryFn: async () => {
      try {
        const response = await treatmentPlanApi.getTreatmentPlans({
          page,
          pageSize,
          search,
          generatedBy,
          dateRange,
          customStartDate,
          customEndDate,
          sortBy,
          sortOrder,
          nextPageToken,
        });
        return response;
      } catch (error) {
        console.error("Failed to fetch treatment plans:", error);
        // Return safe default structure
        return { treatmentPlans: [], pagination: {} as PaginationDto };
      }
    },
    enabled,
  });

  return {
    treatmentPlans: data?.treatmentPlans || [],
    pagination: data?.pagination as PaginationDto,
    isLoading,
  };
};

export interface UseTreatmentPlanDetailParams {
  treatmentId: string;
  enabled?: boolean;
}

export interface UseTreatmentPlanDetailResult {
  treatmentPlan: TreatmentPlanDto | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const useTreatmentPlanDetail = ({
  treatmentId,
  enabled = true,
}: UseTreatmentPlanDetailParams): TreatmentPlanDto & {
  refetch: () => void;
} => {
  const { data, refetch } = useQuery<TreatmentPlanDto>({
    queryKey: ["treatmentPlan", treatmentId],
    queryFn: async () => {
      const response = await treatmentPlanApi.getTreatmentPlanById({
        id: treatmentId,
      });
      return response;
    },
    enabled,
  });
  return { ...(data as TreatmentPlanDto), refetch: () => refetch() };
};

export interface UseSendFeedbackResult {
  sendFeedback: (treatmentPlanId: string, feedback: string) => Promise<void>;
  isSending: boolean;
  error: string | null;
}

export const useSendFeedback = (): UseSendFeedbackResult => {
  const { showSuccess, showError } = useToast();

  const {
    mutateAsync: sendFeedbackMutation,
    isPending: isSending,
    error: mutationError,
  } = useMutation({
    mutationFn: async ({
      treatmentPlanId,
      feedback,
    }: {
      treatmentPlanId: string;
      feedback: string;
    }) => {
      try {
        await treatmentPlanApi.submitFeedback({
          submitFeedbackDto: {
            treatmentPlanId,
            feedback,
          },
        });
      } catch (error) {
        console.error("Failed to send feedback:", error);
        throw error;
      }
    },
    onSuccess: () => {
      showSuccess("Success", "Feedback sent successfully.");
    },
    onError: (error) => {
      showError(
        "Error",
        error instanceof Error
          ? error.message
          : "Failed to send feedback. Please try again."
      );
    },
  });

  const error = mutationError
    ? mutationError instanceof Error
      ? mutationError.message
      : "Unknown error"
    : null;

  return {
    sendFeedback: async (treatmentPlanId: string, feedback: string) => {
      await sendFeedbackMutation({ treatmentPlanId, feedback });
    },
    isSending,
    error,
  };
};

export interface UseGeneratePdfResult {
  generatePdf: (
    treatmentPlanId: string,
    onSuccess?: () => void
  ) => Promise<void>;
  isGenerating: boolean;
  error: string | null;
}

export const useGeneratePdf = (): UseGeneratePdfResult => {
  const { showSuccess, showError } = useToast();

  const {
    mutateAsync: generatePdfMutation,
    isPending: isGenerating,
    error: mutationError,
  } = useMutation({
    mutationFn: async (treatmentPlanId: string) => {
      try {
        await treatmentPlanApi.generatePdf({
          generatePdfDto: {
            treatmentPlanId,
          },
        });
      } catch (error) {
        console.error("Failed to generate PDF:", error);
        throw error;
      }
    },
    onSuccess: () => {
      showSuccess("Success", "PDF generation has been initiated successfully.");
    },
    onError: (error) => {
      showError(
        "Error",
        error instanceof Error
          ? error.message
          : "Failed to generate PDF. Please try again."
      );
    },
  });

  const error = mutationError
    ? mutationError instanceof Error
      ? mutationError.message
      : "Unknown error"
    : null;

  return {
    generatePdf: async (treatmentPlanId: string, onSuccess?: () => void) => {
      await generatePdfMutation(treatmentPlanId);
      if (onSuccess) {
        onSuccess();
      }
    },
    isGenerating,
    error,
  };
};
export default useTreatmentPlan;
